/**
 * perfStats — 성능 지표 평활(EMA) + 포맷 순수 코어. Phaser/DOM 의존 0 → 단위테스트 100% 커버.
 *
 * 원시 샘플(매 프레임 fps·frameMs·섹션 ms)은 노이즈가 커서 그대로 표시하면 읽기 어렵다.
 * 지수이동평균(EMA)으로 평활해 안정된 수치를 보여준다. 섹션 ms는 update() 구간별 처리시간.
 */
import type { PerfFrameSample, PerfSnapshot } from "./types.js";

/** EMA 1스텝: 첫 값은 그대로, 이후 prev + alpha*(v-prev). alpha↑ = 반응 빠름/노이즈↑. */
export function ema(prev: number | null, v: number, alpha: number): number {
  return prev === null ? v : prev + alpha * (v - prev);
}

export interface PerfStatsOptions {
  /** 섹션/heap EMA 평활 계수 0~1 (기본 0.1 — 안정, 약 10프레임 평활). */
  alpha?: number;
  /** fps·frameMs 전용 빠른 EMA 계수 (기본 0.35 — 렉 즉시 반응). */
  fpsAlpha?: number;
  /** min fps 윈도 프레임 수 (기본 60 ≈ 1초) — 최근 구간 worst fps 추적. */
  fpsWindowFrames?: number;
}

export interface PerfStats {
  /** 섹션 처리시간 누적 — 같은 라벨 반복 호출 시 EMA 평활. 등록 순서 보존. */
  mark(section: string, ms: number): void;
  /** 프레임 전역 지표 갱신. */
  frame(sample: PerfFrameSample): void;
  /** 현재 평활 스냅샷. */
  snapshot(): PerfSnapshot;
  /** 표시용 라인 배열 (오버레이/콘솔 공용). */
  format(): string[];
}

export function createPerfStats(opts: PerfStatsOptions = {}): PerfStats {
  const alpha = opts.alpha ?? 0.1;
  const fpsAlpha = opts.fpsAlpha ?? 0.35;
  const windowSize = opts.fpsWindowFrames ?? 60;
  let fps: number | null = null;
  let frameMs: number | null = null;
  let heapMB: number | null = null;
  let counts: Record<string, number> = {};
  // 최근 instant fps 윈도 — 최저(worst)로 렉 스파이크 즉시 가시화. 평균 EMA는 천천히 회복돼 worst를 가린다.
  const fpsWindow: number[] = [];
  // Map으로 등록(첫 등장) 순서 보존 — 표시 순서가 update() 호출 순서와 일치하게.
  const sections = new Map<string, number>();

  function mark(section: string, ms: number): void {
    sections.set(section, ema(sections.get(section) ?? null, ms, alpha));
  }

  function frame(sample: PerfFrameSample): void {
    // fps·frameMs는 빠른 alpha — 렉이 오면 즉시 내려가야 한다(actualFps 이중 평활 지연 제거).
    fps = ema(fps, sample.fps, fpsAlpha);
    frameMs = ema(frameMs, sample.frameMs, fpsAlpha);
    fpsWindow.push(sample.fps);
    if (fpsWindow.length > windowSize) fpsWindow.shift();
    // heap은 미지원이면 null 유지 — 안정 평활(가용 시 최근 평활값).
    heapMB = sample.heapMB === null ? heapMB : ema(heapMB, sample.heapMB, alpha);
    counts = sample.counts;
  }

  function snapshot(): PerfSnapshot {
    return {
      fps: fps ?? 0,
      lowFps: fpsWindow.length > 0 ? Math.min(...fpsWindow) : (fps ?? 0),
      frameMs: frameMs ?? 0,
      heapMB,
      counts: { ...counts },
      sections: Object.fromEntries(sections),
    };
  }

  function format(): string[] {
    const s = snapshot();
    const lines: string[] = [];
    lines.push(`FPS ${s.fps.toFixed(1)} (low ${s.lowFps.toFixed(0)})  ${s.frameMs.toFixed(1)}ms`);
    lines.push(`HEAP ${s.heapMB === null ? "—" : `${s.heapMB.toFixed(0)}MB`}`);
    const countParts = Object.entries(s.counts).map(([k, v]) => `${k} ${v}`);
    if (countParts.length > 0) lines.push(countParts.join("  "));
    const sectionParts = Object.entries(s.sections).map(([k, v]) => `${k} ${v.toFixed(2)}`);
    if (sectionParts.length > 0) lines.push(sectionParts.join("  "));
    return lines;
  }

  return { mark, frame, snapshot, format };
}
